import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Popover, useTheme } from '@mui/material';
import { DotsThreeVertical } from '@phosphor-icons/react';
import { IconButton } from 'components/button/IconButton';
import type { TableSettingsAction, TableState } from '../types';
import { useEvent } from 'lib/segment';
import { useTableExport } from '../hooks/useExport';
import type { ExportType } from 'features/misc/table/types';
import { TableExportButton } from './export/ExportButton';
import { TableExportModal } from './export/ExportModal';
import type { ColumnDef } from '@tanstack/react-table';

export interface SettingsButtonProps {
  disabled?: boolean;
  settingsActions?: TableSettingsAction[];
  tableId?: string;
  tableState?: TableState;
  columns?: ColumnDef<unknown>[];
  filters?: import('types/table').FilterParams;
}

/**
 * Settings button component that displays a popover with custom content when clicked.
 * Uses IconButton with a three dots vertical icon.
 * @param disabled - Whether the button should be disabled
 * @param settingsActions - Array of predefined actions (export, custom)
 * @param tableId - Required for export functionality
 * @param tableState - Current table state for export
 * @param columns - Table columns for export
 */
export const SettingsButton = ({
  disabled = false,
  settingsActions,
  tableId,
  tableState,
  columns,
  filters,
}: SettingsButtonProps) => {
  const theme = useTheme();
  const event = useEvent();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [currentExportAction, setCurrentExportAction] = useState<TableSettingsAction | null>(null);

  const isOpen = Boolean(anchorEl);

  const exportAction = settingsActions?.find(action => action.type === 'export') as
    | (TableSettingsAction & { type: 'export' })
    | undefined;

  const { exportTable, isPending } = useTableExport({
    apiEndpoint: exportAction?.apiEndpoint || null,
    onSuccess: () => {
      setExportModalOpen(false);
      exportAction?.onSuccess?.();
    },
    onError: exportAction?.onError,
    filters,
    vars: exportAction?.vars,
  });

  const handleClick = (events: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(events.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleKeyPress = (events: React.KeyboardEvent<HTMLElement>) => {
    const isEnter = events.key === 'Enter';
    const isEscape = events.key === 'Escape';
    if ((isEnter || isEscape) && isOpen) {
      handleClose();
    }
  };

  const handleExportClick = (action: TableSettingsAction & { type: 'export' }) => {
    setCurrentExportAction(action);
    setExportModalOpen(true);
    handleClose();

    // Track modal open event
    event('View', {
      item: tableId || 'Table',
      action: 'Export Modal Open',
      target_type: 'Modal',
      action_source: 'Table settings',
    });
  };

  const getVisibleColumns = (): string[] => {
    if (!tableId || !columns) return [];

    try {
      const columnVisibility = localStorage.getItem(`${tableId}-tableColumnVisibility`);
      const columnOrder = localStorage.getItem(`${tableId}-tableColumnOrder`);
      const pinned = localStorage.getItem(`${tableId}-tableColumnPinned`);

      const visibility = columnVisibility ? JSON.parse(columnVisibility) : {};
      const allColumnIds = columns
        .map(col => col.id as string)
        .filter(Boolean)
        .filter(colId => colId !== 'Selector');

      const visibleColumnIds = allColumnIds.filter(colId => visibility[colId] !== false);

      let leftPinnedColumns: string[] = [];
      if (pinned) {
        const pinnedData = JSON.parse(pinned);
        if (pinnedData?.left && Array.isArray(pinnedData.left)) {
          leftPinnedColumns = pinnedData.left.filter(
            (colId: string) => visibleColumnIds.includes(colId) && colId !== 'Selector'
          );
        }
      }

      if (columnOrder) {
        const order = JSON.parse(columnOrder);
        if (Array.isArray(order) && order.length > 0) {
          const orderedVisibleColumns = order.filter(
            colId =>
              visibleColumnIds.includes(colId) &&
              visibility[colId] !== false &&
              colId !== 'Selector'
          );
          const remainingVisible = visibleColumnIds.filter(colId => !order.includes(colId));

          // remove pinned columns from the ordered columns since they'll be added at the front
          const orderedWithoutPinned = orderedVisibleColumns.filter(
            colId => !leftPinnedColumns.includes(colId)
          );
          const remainingWithoutPinned = remainingVisible.filter(
            colId => !leftPinnedColumns.includes(colId)
          );

          return [...leftPinnedColumns, ...orderedWithoutPinned, ...remainingWithoutPinned];
        }
      }

      // no custom order, but still need to move pinned columns to front
      const nonPinnedVisible = visibleColumnIds.filter(colId => !leftPinnedColumns.includes(colId));
      return [...leftPinnedColumns, ...nonPinnedVisible];
    } catch (error) {
      // Fallback: return all column IDs
      return (
        columns
          ?.map(col => col.id as string)
          .filter(Boolean)
          .filter(colId => colId !== 'Selector') || []
      );
    }
  };

  const handleExport = (exportType: ExportType) => {
    if (!currentExportAction || currentExportAction.type !== 'export') return;

    const selectedColumns: string[] = exportType === 'DisplayedData' ? getVisibleColumns() : [];

    exportTable({
      exportType,
      selectedColumns,
      filterCriteria: tableState || {},
    });

    event('Action', {
      item: tableId || 'Table',
      action: 'Export',
      target_type: 'Table Data',
      action_source: 'Table settings',
      target_id: exportType,
      target_name: exportType,
    });
  };

  const id = isOpen ? 'settings-popover' : undefined;

  if (!settingsActions || settingsActions.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        size="lg"
        onClick={handleClick}
        disabled={disabled}
        aria-describedby={id}
        aria-label="Settings"
        sx={{
          ...(isOpen
            ? {
                background: theme.palette.bg.brandPrimary,
                color: theme.palette.text.brandSecondary,
              }
            : {}),
        }}
      >
        <DotsThreeVertical size={20} color="currentColor" fill="currentColor" />
      </IconButton>

      <StyledPopover
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        onKeyDown={handleKeyPress}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableRestoreFocus
      >
        {settingsActions.map((action, index) => {
          if (action.type === 'export') {
            return (
              <TableExportButton
                key={`${action.type}-${action.tableId || index}`}
                onClick={() => handleExportClick(action)}
                isLoading={isPending}
                disabled={disabled}
              />
            );
          } else if (action.type === 'custom') {
            return <div key={`${action.type}-${action.tableId || index}`} />;
          }
          return null;
        })}
      </StyledPopover>

      <TableExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExport}
        isLoading={isPending}
      />
    </>
  );
};

const StyledPopover = styled(Popover)`
  .MuiPopover-paper {
    min-width: 217px;
    padding: 0 1px;
    margin-top: 5px;
  }
`;
